import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { newsService, NewsArticle } from '../services/newsService';
import SectionTitle from './shared/SectionTitle';

const NewsContainer = styled.section`
  width: 100%;
  padding: 80px 5%;
  background: ${theme.colors.light_gray};

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;

const NewsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;

  @media (max-width: 768px) {
    gap: 32px;
  }

  @media (max-width: 480px) {
    gap: 24px;
  }
`;

const NewsHeader = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;


const NewsSubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: ${theme.colors.gray};
  margin: 0;
  max-width: 600px;
  align-self: center;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 27px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const NewsCard = styled.article`
  background: ${theme.colors.white};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0px 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const NewsImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 200px;
          background-image: url(${props => props.imageUrl || '/shared_assets/images/news-placeholder.jpg'});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 0, 0, 0.1) 50%,
      rgba(0, 0, 0, 0.3) 100%
    );
  }
`;

const NewsCardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    gap: 10px;
  }
`;

const NewsCardTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 26px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const NewsCardDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.gray};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 20px;
  }
`;

const NewsCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding-top: 12px;
  }

  @media (max-width: 480px) {
    padding-top: 10px;
  }
`;

const NewsSource = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.outchou_green};
  background: rgba(57, 181, 157, 0.1);
  padding: 4px 12px;
  border-radius: 20px;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 18px;
    padding: 3px 10px;
  }
`;

const NewsDate = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.gray};

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
  font-family: ${theme.fonts.body};
  font-size: 18px;
  color: ${theme.colors.gray};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
  text-align: center;
`;

const ErrorMessage = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 18px;
  color: ${theme.colors.gray};
  margin: 0;
`;

const RetryButton = styled.button`
  background: ${theme.colors.outchou_green};
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  color: ${theme.colors.white};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${theme.colors.outchou_green_hover};
  }
`;

const LoadMoreButton = styled.button`
  background: ${theme.colors.outchou_blue};
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 18px;
  color: ${theme.colors.white};
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: center;

  &:hover {
    background: ${theme.colors.outchou_blue_hover};
  }

  &:disabled {
    background: ${theme.colors.gray};
    cursor: not-allowed;
  }
`;

interface NewsSectionProps {
  maxArticles?: number;
  showLoadMore?: boolean;
}

const NewsSection: React.FC<NewsSectionProps> = ({ 
  maxArticles = 6, 
  showLoadMore = false 
}) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNews = async (pageNum: number = 1, append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // First check if backend is available
      const isBackendHealthy = await newsService.checkBackendHealth();
      if (!isBackendHealthy) {
        throw new Error('Le serveur backend n\'est pas accessible. Veuillez vérifier que le serveur Flask est en cours d\'exécution.');
      }
      
      // Use headlines for landing page (when showLoadMore is false), full news for news page
      const response = showLoadMore 
        ? await newsService.getAlgeriaNews(maxArticles, pageNum)
        : await newsService.getAlgeriaHeadlines(maxArticles);
      
      if (response.status === 'success') {
        if (append) {
          setArticles(prev => [...prev, ...response.articles]);
        } else {
          setArticles(response.articles);
        }
        
        setHasMore(response.articles.length === maxArticles);
        setPage(pageNum);
      } else {
        throw new Error(response.message || 'Erreur lors de la récupération des actualités');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err instanceof Error ? err.message : 'Une erreur inattendue s\'est produite');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [maxArticles]);

  const handleLoadMore = () => {
    fetchNews(page + 1, true);
  };

  const handleRetry = () => {
    fetchNews();
  };

  const handleArticleClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading && articles.length === 0) {
    return (
      <NewsContainer>
        <NewsContent>
          <NewsHeader>
            <SectionTitle>Actualités d'Algérie</SectionTitle>
            <NewsSubtitle>
              Découvrez les dernières nouvelles et événements importants en Algérie
            </NewsSubtitle>
          </NewsHeader>
          <LoadingState>Chargement des actualités...</LoadingState>
        </NewsContent>
      </NewsContainer>
    );
  }

  if (error && articles.length === 0) {
    return (
      <NewsContainer>
        <NewsContent>
          <NewsHeader>
            <SectionTitle>Actualités d'Algérie</SectionTitle>
            <NewsSubtitle>
              Découvrez les dernières nouvelles et événements importants en Algérie
            </NewsSubtitle>
          </NewsHeader>
          <ErrorState>
            <ErrorMessage>{error}</ErrorMessage>
            <RetryButton onClick={handleRetry}>Réessayer</RetryButton>
          </ErrorState>
        </NewsContent>
      </NewsContainer>
    );
  }

  return (
    <NewsContainer>
      <NewsContent>
        <NewsHeader>
          <SectionTitle>Actualités d'Algérie</SectionTitle>
          <NewsSubtitle>
            Découvrez les dernières nouvelles et événements importants en Algérie
          </NewsSubtitle>
        </NewsHeader>

        <NewsGrid>
          {articles.map((article) => (
            <NewsCard 
              key={article.id} 
              onClick={() => handleArticleClick(article.url)}
            >
              <NewsImage imageUrl={article.imageUrl} />
              <NewsCardContent>
                <NewsCardTitle>{article.title}</NewsCardTitle>
                <NewsCardDescription>
                  {newsService.truncateText(article.description, 120)}
                </NewsCardDescription>
                <NewsCardMeta>
                  <NewsSource>{article.source}</NewsSource>
                  <NewsDate>
                    {newsService.formatDate(article.publishedAt)}
                  </NewsDate>
                </NewsCardMeta>
              </NewsCardContent>
            </NewsCard>
          ))}
        </NewsGrid>

        {showLoadMore && hasMore && !loading && (
          <LoadMoreButton onClick={handleLoadMore}>
            Charger plus d'articles
          </LoadMoreButton>
        )}

        {loading && articles.length > 0 && (
          <LoadingState>Chargement...</LoadingState>
        )}
      </NewsContent>
    </NewsContainer>
  );
};

export default NewsSection; 